import { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    MenuItem,
    Select,
    Tooltip,
    type SelectChangeEvent,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { useCommonStyles } from '../../styles/GenericStyle';
import { GenericConstant } from '../../utils/GenericConstant';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import type { LabelTemplate } from '../../api/vegApl/labelTemplate';
export type TemplateSelectionMode = 'noTemplate' | 'changeTemplate';

interface TemplateSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (selectedTemplate: string) => void;
    onAddNewTemplate?: () => void;
    mode: TemplateSelectionMode;
    cropName?: string;
    cropCode?: string;
    siteName?: string;
    templateOptions?: LabelTemplate[];
    selectedTemplate: string;
    onTemplateChange: (value: string) => void;
    /** Override the dialog title for noTemplate mode. Defaults to "Print SP Lot Label" */
    noTemplateTitle?: string;
}

const styles = {
    paper: { borderRadius: '16px', maxWidth: 600, p: 0, overflow: 'hidden' },
    title: { pb: 0, pt: 3, px: 3 },
    titleText: { fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a' },
    content: { px: 3, pb: 3, pt: 1 },
    innerBox: { border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, backgroundColor: '#F9FAFB' },
    infoRow: { display: 'flex', gap: 1, alignItems: 'center', mb: 2 },
    infoIcon: { fontSize: 20, color: '#555' },
    infoTitle: { fontWeight: 400, fontSize: '0.9rem', color: '#333' },
    infoDesc: { fontSize: '0.8rem', color: '#666', mt: 0.5 },
    selectRow: { display: 'flex', gap: 1.5, alignItems: 'center', mt: 1 },
    addBtn: {
        textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', borderRadius: '8px',
        border: '1px solid #ccc', color: '#333', px: 2, py: 0.8,
        '&:hover': { backgroundColor: '#fafafa', border: '1px solid #999' },
    },
    actions: { px: 0, pb: 0, pt: 2, gap: 1.5, justifyContent: 'flex-end' },
    cancelBtn: {
        textTransform: 'none', fontWeight: 600, border: '1px solid #d0d0d0', py: 0.8, px: 3,
        fontSize: '0.85rem', color: '#333', borderRadius: '8px',
        '&:hover': { backgroundColor: '#f5f5f5', boxShadow: 'none', border: '1px solid #999' },
    },
    confirmBtn: {
        textTransform: 'none', fontWeight: 600, py: 0.8, px: 3, fontSize: '0.85rem',
        background: '#1a1a1a', borderRadius: '8px', boxShadow: 'none', color: '#fff',
        '&:hover': { background: '#333', boxShadow: 'none' },
        '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
    },
} as const;

const dialogConfig = {
    noTemplate: {
        title: 'Print SP Lot Label',
        getInfoTitle: (crop: string, site: string) => `No Template Found for ${crop} - ${site}`,
        description: 'There is no template configured for this crop and site combination. Please add a new template or select an existing template manually.',
        selectLabel: 'Select Template',
        showAddNew: true,
    },
    changeTemplate: {
        title: 'Change Template',
        getInfoTitle: () => 'Select template from master list',
        description: '',
        selectLabel: 'Select Existing Template',
        showAddNew: false,
    },
};

const TemplateSelectionDialog = ({
    open,
    onClose,
    onConfirm,
    onAddNewTemplate,
    mode,
    cropName = '',
    cropCode = '',
    siteName = '',
    templateOptions = [],
    selectedTemplate,
    onTemplateChange,
    noTemplateTitle,
}: TemplateSelectionDialogProps) => {
    const classes = useCommonStyles();
    const config = dialogConfig[mode];
    const resolvedTitle = mode === 'noTemplate' && noTemplateTitle ? noTemplateTitle : config.title;
    const { menuPosition, calculatePosition } = useDropdownPosition(300);
    // Filter templates by crop code from the selected row
    const filteredTemplates = useMemo(() => {
        const matchKey = cropCode || cropName;
        if (!matchKey) return templateOptions;
        const matchLower = matchKey.toLowerCase();
        const filtered = templateOptions.filter(
            (t) => t.crop_code && t.crop_code.toLowerCase() === matchLower && t.site_code && t.site_code == siteName 
        );
        // Fall back to all templates if none match the crop
        return filtered.length > 0 ? filtered : templateOptions;
    }, [templateOptions, cropCode, cropName]);

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        onTemplateChange(event.target.value);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: styles.paper }}>
            <DialogTitle sx={styles.title}>
                <Typography variant="h6" component="span" sx={styles.titleText}>{resolvedTitle}</Typography>
            </DialogTitle>

            <DialogContent sx={styles.content}>
                <Box sx={styles.innerBox}>
                    <Box sx={styles.infoRow}>
                        <InfoOutlinedIcon sx={styles.infoIcon} />
                        <Box>
                            <Typography variant="body1" sx={styles.infoTitle}>
                                {config.getInfoTitle(cropName, siteName)}
                            </Typography>
                            {config.description && (
                                <Typography variant="body2" sx={styles.infoDesc}>
                                    {config.description}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={styles.selectRow}>
                        {config.showAddNew && (
                            <Tooltip title="Not supported currently" arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        disableRipple
                                        disabled
                                        onClick={onAddNewTemplate}
                                        sx={styles.addBtn}
                                    >
                                        Add New Template
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        <Select
                            value={selectedTemplate}
                            onChange={handleSelectChange}
                            displayEmpty
                            size="small"
                            IconComponent={KeyboardArrowDownOutlinedIcon}
                            onOpen={calculatePosition}
                            sx={(theme) => ({
                                flex: 1,
                                height: 40,
                                borderRadius: '4px',
                                ...MtComStyleObj(theme).fieldFocusHoverStyle,
                                '& .MuiSelect-icon': {
                                    fontSize: 25,
                                    color: '#000000',
                                },
                            })}
                            MenuProps={{
                                anchorOrigin: menuPosition.anchorOrigin,
                                transformOrigin: menuPosition.transformOrigin,
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        marginTop: menuPosition.anchorOrigin.vertical === 'top' ? -8 : 8,
                                    },
                                },
                            }}
                            renderValue={(selected) => {
                                return selected
                                    ? <Typography component="span" sx={{ ...GenericConstant.DROPDOWNSTYLE }}>
                                        {filteredTemplates.find((ele) => String(ele?.template_id) === selected)?.template_name}
                                    </Typography>
                                    : <Typography component="span" className={classes.labelNoto16Bold400} color="#808080">
                                        {config.selectLabel}
                                    </Typography>;
                            }}>
                            {filteredTemplates?.map((opt) => (
                                <MenuItem disableRipple sx={{ ...GenericConstant.DROPDOWNSTYLE }} key={opt.template_id} value={String(opt.template_id)}>{opt.template_name}</MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <DialogActions sx={styles.actions}>
                        <Button onClick={onClose} variant="outlined" disableRipple sx={styles.cancelBtn}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => onConfirm(selectedTemplate)}
                            variant="contained"
                            disableRipple
                            disabled={!selectedTemplate}
                            sx={styles.confirmBtn}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TemplateSelectionDialog;
